import React, { useState } from 'react';
import { toast } from 'sonner';
import { Copy, Sparkles, Loader2, Check } from 'lucide-react';

const PLATFORMS = [
  { id: 'instagram', label: 'Instagram' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'x', label: 'X' },
];

const fullText = (c) => [c.title, c.caption, (c.hashtags || []).map((h) => `#${h.replace(/^#/, '')}`).join(' ')].filter(Boolean).join('\n\n');

export default function LaunchCaptions({ captions, onGenerate, onSave, busy }) {
  const [tab, setTab] = useState('instagram');
  const [draft, setDraft] = useState(null);
  const has = captions && Object.keys(captions).length > 0;
  const cur = captions?.[tab] || {};
  const text = draft ?? fullText(cur);

  const copy = async () => {
    try { await navigator.clipboard.writeText(text); toast.success(`${PLATFORMS.find((p) => p.id === tab).label} post copied.`); }
    catch (e) { toast.error('Could not copy.'); }
  };
  const save = async () => {
    const parts = text.split(/\n\n+/);
    const tags = parts.length > 1 && /^#/.test(parts[parts.length - 1].trim()) ? parts.pop().split(/\s+/).map((h) => h.replace(/^#/, '')).filter(Boolean) : cur.hashtags || [];
    const next = { ...captions, [tab]: { ...cur, caption: parts.join('\n\n'), hashtags: tags } };
    await onSave(next); setDraft(null);
  };

  return (
    <div className="rounded-2xl border hairline p-5" data-testid="launch-captions">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="flex items-center gap-2 text-sm text-cream"><Sparkles size={15} className="text-gold" /> Captions &amp; hashtags</p>
        <button onClick={onGenerate} disabled={busy} data-testid="captions-generate-btn" className="btn-ghost rounded-full px-4 py-2 text-xs flex items-center gap-2 disabled:opacity-50">{busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} {has ? 'Rewrite all' : 'Write my captions'}</button>
      </div>
      <p className="text-xs text-cream/50 mt-1">Written in your voice for each platform. Edit anything, then copy and paste when you post.</p>
      {has && (
        <>
          <div className="mt-4 flex gap-1.5 flex-wrap">
            {PLATFORMS.map((p) => (
              <button key={p.id} onClick={() => { setTab(p.id); setDraft(null); }} data-testid={`captions-tab-${p.id}`} className={`rounded-full px-3.5 py-1.5 text-xs transition-colors ${tab === p.id ? 'bg-gold text-ink' : 'border hairline text-cream/65 hover:text-cream'}`}>{p.label}</button>
            ))}
          </div>
          <textarea value={text} onChange={(e) => setDraft(e.target.value)} rows={8} data-testid="captions-text" className="mt-3 w-full rounded-xl border hairline bg-panel2/50 px-4 py-3 text-sm text-cream outline-none focus:border-gold/50 transition-colors resize-y leading-relaxed" />
          <div className="mt-2 flex items-center gap-2 justify-end">
            {draft !== null && <button onClick={save} data-testid="captions-save-btn" className="btn-ghost rounded-full px-4 py-2 text-xs flex items-center gap-1.5"><Check size={12} /> Save edits</button>}
            <button onClick={copy} data-testid="captions-copy-btn" className="btn-gold rounded-full px-4 py-2 text-xs flex items-center gap-1.5"><Copy size={12} /> Copy post</button>
          </div>
        </>
      )}
    </div>
  );
}
